import React, { useCallback } from 'react';
import { useFirebase } from '@/services/analytics/use-firebase';
import { useEducation, useSiteContent } from '@personal-portfolio/queries';
import { PageHeader, PageState, PageErrorState, Reveal } from '@/shared/ui';
import { EducationCard } from './components/education-card';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation } from 'react-router';
import type { EducationCardOnLinkClicked } from './components/education-card';
import { toErrorMessage } from '@/shared/lib/query-error';

export const EducationPage: React.FC = () => {
  const {
    data: educationData,
    isPending: educationLoading,
    isFetching: educationFetching,
    isPaused: educationPaused,
    error: educationError,
    refetch: refetchEducation,
  } = useEducation();
  const error = toErrorMessage(educationError, educationPaused);
  const { trackEvent } = useFirebase();
  const location = useLocation();
  const content = useSiteContent();
  const page = content.pages.education;

  const handleLinkClick = useCallback<EducationCardOnLinkClicked>(
    (education, link) => {
      trackEvent('education_link_clicked', {
        education_institution: education.institution,
        education_degree: education.degree,
        link_label: link.label || 'Link',
        link_url: link.url,
        link_type: link.type || 'external',
      });
    },
    [trackEvent],
  );

  const handleRetry = () => {
    if (educationFetching) return;
    void refetchEducation();
  };

  const renderLoading = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <div className="animate-pulse">
        <div className="mb-8 h-32 rounded bg-muted"></div>
        <div className="space-y-4">
          <div className="h-24 rounded-xl bg-muted"></div>
          <div className="h-24 rounded-xl bg-muted"></div>
          <div className="h-24 rounded-xl bg-muted"></div>
        </div>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <PageErrorState onRetry={handleRetry} message={error ?? undefined} />
    </>
  );

  const renderContent = () => {
    if (!educationData?.length) {
      return (
        <>
          <PageHeader title={page.title} subtitle={page.subtitle} />
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-6xl text-muted-foreground">
                <span role="img" aria-label="Graduation cap">
                  🎓
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                {page.empty.title}
              </h3>
              <p className="text-muted-foreground">{page.empty.message}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader title={page.title} subtitle={page.subtitle} />

        <div className="space-y-8">
          {educationData.map((edu) => (
            <Reveal key={edu.id}>
              <EducationCard {...edu} onLinkClick={handleLinkClick} />
            </Reveal>
          ))}
        </div>

        <div className="mt-16 space-y-12">
          <div>
            <h2 className="mb-8 text-2xl leading-snug font-semibold tracking-tight text-foreground">
              {page.focusAreasHeading}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {page.focusAreas.map((area, index) => (
                <Reveal key={area.title} index={index}>
                  <div className="h-full rounded-xl border border-border bg-card p-6 shadow-xs transition-shadow duration-200 hover:shadow-md">
                    <h3 className="mb-3 text-lg font-semibold text-card-foreground">
                      {area.title}
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">
                      {area.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-8 text-2xl leading-snug font-semibold tracking-tight text-foreground">
              {page.philosophyHeading}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {page.philosophy.map((item, index) => (
                <Reveal key={item.title} index={index}>
                  <div className="h-full rounded-xl border border-border bg-card p-6 shadow-xs transition-shadow duration-200 hover:shadow-md">
                    <h3 className="mb-3 text-lg font-semibold text-card-foreground">
                      {item.title}
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">
                      {item.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{page.metaTitle}</title>
        <link
          rel="canonical"
          href={`${content.site.url}${location.pathname}`}
        />
      </Helmet>
      <PageState
        loading={educationLoading}
        error={error}
        hasData={!!educationData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
};
